/**
 * Derives parent J-code filter inputs from persisted auth-on-file entries.
 * @module siblingOrderFilter/buildFilterJCodesFromAuthOnFile
 */
import moment from "moment";
import { AuthOnFile } from "../../../data-model/medicalPaOrdersModel";
import { getAllJCodesFromAuthOnFile } from "../../../pages/singleOrderLayout/utils/existingDocsToLs";
import { FilterJCodeInput } from "./types";

/**
 * Formats a raw auth-on-file date into the `MM/DD/YYYY` string consumed by the
 * sibling-order filtering pipeline.
 *
 * @param value - Raw date string from auth-on-file J-code details
 * @returns Formatted date, or an empty string when absent or invalid
 */
const toFormattedDate = (value?: string): string =>
  value && moment(value).isValid() ? moment(value).format("MM/DD/YYYY") : "";

/**
 * Maps auth-on-file J-code entries to {@link FilterJCodeInput}, carrying only
 * the fields the sibling-order filtering pipeline consumes.
 *
 * @param authOnFile - Parent order auth-on-file payload, or undefined while loading
 * @returns Parent J-codes limited to the pipeline-relevant fields
 */
export const buildFilterJCodesFromAuthOnFile = (
  authOnFile?: AuthOnFile,
): FilterJCodeInput[] => {
  const jCodes = getAllJCodesFromAuthOnFile(authOnFile) ?? [];

  return jCodes.map((item) => ({
    j_code: item.j_code ?? "",
    auth_status: item.auth_status ?? "",
    start_date: toFormattedDate(item.start_date),
    end_date: toFormattedDate(item.end_date),
    visits_remaining:
      typeof item.visits_remaining === "number"
        ? item.visits_remaining
        : parseInt(String(item.visits_remaining ?? "0"), 10) || 0,
  }));
};
